import React, {useCallback, useEffect, useState} from 'react';
import {Download, Plus} from 'lucide-react';
import Button from '@/shared/components/Button';
import AdminService from '@/modules/admin/services/adminService';
import {useAdminNotifications} from '@/modules/admin/contexts/AdminNotificationContext';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {isUserAdmin} from '@/shared/utils/tokenUtils';
import {logError} from '@/shared/utils/logger';
import {downloadSqlFile} from '@/shared/utils/downloadFile';
import {
    BillingType,
    BioMatch,
    BioMatchType,
    BiomarkerRule,
    RecommendationType,
    RecommendedAssay,
    RecommendedProduct,
    RiskTier,
    RuleSource,
    RuleType,
} from '../types';
import {BioMatchList} from './BioMatchList';
import {BioMatchModal} from './BioMatchModal';

const isValidRecommendationType = (value: unknown): value is RecommendationType =>
    typeof value === 'string' && (Object.values(RecommendationType) as string[]).includes(value);

const isValidRiskTier = (value: unknown): value is RiskTier =>
    typeof value === 'string' && (Object.values(RiskTier) as string[]).includes(value);

const isValidBillingType = (value: unknown): value is BillingType =>
    typeof value === 'string' && (Object.values(BillingType) as string[]).includes(value);

const normalizeBioMatch = (rec: any): BioMatch => {
    const biomarkers: BiomarkerRule[] = Array.isArray(rec?.biomarkers)
        ? (rec.biomarkers as BiomarkerRule[]).map((b) => ({
              test_type: (b.test_type ?? '').toString(),
              assay_code: b.assay_code ?? null,
              rule_type: b.rule_type as RuleType,
              group_index: typeof b.group_index === 'number' ? b.group_index : 0,
              rule_source: (b.rule_source as RuleSource) || RuleSource.BIOMARKER,
              measurement_key: b.measurement_key ?? null,
              min_value: b.min_value ?? null,
              max_value: b.max_value ?? null,
              threshold_value: b.threshold_value ?? null,
              rule_expression: b.rule_expression ?? '',
              gender: b.gender ?? null,
          }))
        : [
              {
                  test_type: rec.test_type ?? '',
                  assay_code: rec.assay_code ?? null,
                  rule_type: rec.rule_type,
                  group_index: 0,
                  rule_source: RuleSource.BIOMARKER,
                  measurement_key: null,
                  min_value: rec.min_value ?? null,
                  max_value: rec.max_value ?? null,
                  threshold_value: rec.threshold_value ?? null,
                  rule_expression: rec.rule_expression ?? '',
                  gender: rec.gender ?? null,
              },
          ];
    const recommendedAssays: RecommendedAssay[] = Array.isArray(rec?.recommended_assays)
        ? (rec.recommended_assays as RecommendedAssay[]).map((a, idx) => ({
              assay_code: (a?.assay_code ?? '').toString(),
              sort_order: typeof a?.sort_order === 'number' ? a.sort_order : idx,
          }))
        : [];
    const recommendedProducts: RecommendedProduct[] = Array.isArray(rec?.recommended_products)
        ? (rec.recommended_products as RecommendedProduct[]).map((p, idx) => ({
              storefront_product_id: typeof p?.storefront_product_id === 'number' ? p.storefront_product_id : 0,
              sort_order: typeof p?.sort_order === 'number' ? p.sort_order : idx,
          }))
        : [];
    const bmType =
        rec?.bm_type === BioMatchType.RANGE
            ? BioMatchType.RANGE
            : rec?.bm_type === BioMatchType.CURATED
              ? BioMatchType.CURATED
              : rec?.organization_id == null
                ? BioMatchType.RANGE
                : BioMatchType.CURATED;
    return {
        id: rec.id,
        organization_id: typeof rec.organization_id === 'number' ? rec.organization_id : null,
        bm_type: bmType,
        name: rec.name,
        enabled: rec.enabled,
        content: rec.content ?? '',
        image: rec.image || '',
        sup_type: rec.sup_type || '',
        price: rec.price || 0,
        members_price: rec.members_price || 0,
        url: rec.url || '',
        recommendation_type: isValidRecommendationType(rec.recommendation_type)
            ? rec.recommendation_type
            : RecommendationType.SUPPLEMENT,
        risk_tier: isValidRiskTier(rec.risk_tier) ? rec.risk_tier : null,
        priority_weight: typeof rec.priority_weight === 'number' ? rec.priority_weight : null,
        cta_button_label: rec.cta_button_label ?? '',
        cta_body_text: rec.cta_body_text ?? '',
        kit_sku: rec.kit_sku ?? '',
        pathway_url: rec.pathway_url ?? '',
        track_enrollment: Boolean(rec.track_enrollment),
        billing_type: isValidBillingType(rec.billing_type) ? rec.billing_type : BillingType.EMPLOYER_FULL,
        employer_coverage_percent:
            typeof rec.employer_coverage_percent === 'number' ? rec.employer_coverage_percent : null,
        biomarkers,
        recommended_assays: recommendedAssays,
        recommended_products: recommendedProducts,
    };
};

interface BioMatchManagerProps {
    orgId?: number;
    /**
     * Super-admin authoring mode. Lists/edits BioMatches with
     * `organization_id IS NULL` (the "Global Library"). Disables the
     * per-org-only UX bits like the override badge / Global pill.
     */
    globalScope?: boolean;
}

export const BioMatchManager: React.FC<BioMatchManagerProps> = ({orgId, globalScope}) => {
    const {user} = useAuth();
    const {showError, showSuccess} = useAdminNotifications();

    const [items, setItems] = useState<BioMatch[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingValue, setEditingValue] = useState<BioMatch | null>(null);
    const [loadingEditId, setLoadingEditId] = useState<number | null>(null);
    const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());
    const [exporting, setExporting] = useState(false);

    const canManage = isUserAdmin(user?.token);

    const fetchList = useCallback(async () => {
        if (!user?.token) return;
        setLoading(true);
        try {
            const apiData = await AdminService.getAllRecommendations(orgId, {globalScope});
            setItems(apiData.map(normalizeBioMatch));
        } catch (error) {
            logError('Failed to fetch BioMatches', error);
            showError('Failed to fetch BioMatches');
        } finally {
            setLoading(false);
        }
    }, [user?.token, orgId, globalScope, showError]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const openCreate = () => {
        setEditingId(null);
        setEditingValue(null);
        setModalOpen(true);
    };

    const openEdit = async (id: number) => {
        if (loadingEditId != null) return;
        setLoadingEditId(id);
        try {
            const rec = await AdminService.getRecommendation(id, orgId, {globalScope});
            setEditingValue(normalizeBioMatch(rec));
            setEditingId(id);
            setModalOpen(true);
        } catch (error) {
            logError('Failed to load BioMatch for edit', error);
            showError('Failed to load BioMatch for editing');
        } finally {
            setLoadingEditId(null);
        }
    };

    const handleDelete = async (id: number) => {
        if (deletingIds.has(id)) return;
        setDeletingIds((prev) => new Set(prev).add(id));
        try {
            await AdminService.deleteRecommendation(id, orgId, {globalScope});
            showSuccess('BioMatch deleted');
            await fetchList();
        } catch (error) {
            logError('Failed to delete BioMatch', error);
            showError('Failed to delete BioMatch');
        } finally {
            setDeletingIds((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    };

    /**
     * Quick status/billing change from the row's combined dropdown.
     * Routes through the dedicated billing endpoint — for globals the
     * server upserts the per-org override, for owned recs it does a
     * partial update of the rec's status + billing columns.
     */
    const handleStatusChange = async (
        rec: BioMatch,
        next: {enabled: boolean; billing_type: BillingType; employer_coverage_percent: number | null}
    ) => {
        try {
            await AdminService.updateRecommendationBilling(
                rec.id,
                {
                    enabled: next.enabled,
                    billing_type: next.billing_type,
                    employer_coverage_percent: next.employer_coverage_percent,
                },
                orgId,
                {globalScope}
            );
            showSuccess('BioMatch updated');
            await fetchList();
        } catch (error) {
            logError('Failed to update BioMatch status', error);
            showError('Could not update BioMatch');
            throw error;
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const result = await AdminService.exportGlobalBioMatch();
            if (result.emailed) {
                showSuccess(`Exported from ${result.environment} — emailed to ${result.recipients.join(' and ')}.`);
            } else {
                downloadSqlFile(result.filename, result.sql);
                showSuccess(
                    `Email isn't configured here — downloaded ${result.filename} (from ${result.environment}) instead.`
                );
            }
        } catch (error) {
            logError('Failed to export BioMatch library', error);
            const message =
                (error as {data?: {error?: string}; response?: {data?: {error?: string}}})?.data?.error ??
                (error as {response?: {data?: {error?: string}}})?.response?.data?.error;
            showError(message || 'Could not export the BioMatch library.');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="space-y-6">
            <BioMatchModal
                open={modalOpen}
                editingId={editingId}
                initialValue={editingValue}
                orgId={orgId}
                globalScope={globalScope}
                onClose={() => setModalOpen(false)}
                onSaved={fetchList}
            />

            <div className="rounded-lg border border-mld-panel-border bg-mld-menu-background p-6 shadow-sm">
                <div className="mb-6 flex flex-wrap items-start justify-between gap-4 border-b border-mld-panel-border pb-4">
                    <div>
                        <h2 className="text-xl font-semibold text-mld-text-primary">
                            {globalScope ? 'Global BioMatch Library' : 'BioMatch'}
                        </h2>
                        <p className="text-sm text-mld-text-secondary">
                            {globalScope
                                ? 'System-default BioMatches available to every organization. Each tenant can override the billing model.'
                                : 'Biomarker-based supplement recommendations for this organization.'}
                        </p>
                    </div>
                    {canManage && (
                        <div className="flex flex-wrap items-center gap-2">
                            {globalScope && (
                                <Button
                                    type="button"
                                    variant="secondary"
                                    loading={exporting}
                                    onClick={handleExport}
                                    iconLeft={<Download className="h-4 w-4" />}>
                                    Export BioMatch Library
                                </Button>
                            )}
                            <Button type="button" onClick={openCreate} iconLeft={<Plus className="h-4 w-4" />}>
                                {globalScope ? 'Build Global BioMatch' : 'Build Custom BioMatch'}
                            </Button>
                        </div>
                    )}
                </div>

                <BioMatchList
                    items={items}
                    loading={loading}
                    loadingEditId={loadingEditId}
                    deletingIds={deletingIds}
                    canManage={canManage}
                    globalScope={globalScope}
                    onEdit={openEdit}
                    onDelete={handleDelete}
                    onStatusChange={handleStatusChange}
                />
            </div>
        </div>
    );
};
